// Simple EventEmitter implementation for browser compatibility
class SimpleEventEmitter {
  private events: { [key: string]: ((...args: unknown[]) => void)[] } = {};

  on(event: string, listener: (...args: unknown[]) => void): void {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  off(event: string, listener: (...args: unknown[]) => void): void {
    if (!this.events[event]) return;
    const index = this.events[event].indexOf(listener);
    if (index > -1) {
      this.events[event].splice(index, 1);
    }
  }

  emit(event: string, ...args: unknown[]): void {
    if (!this.events[event]) return;
    this.events[event].forEach((listener) => listener(...args));
  }

  removeAllListeners(event?: string): void {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
  }
}

export interface WebSocketMessage {
  type: string;
  payload: unknown;
  timestamp: string;
  id?: string;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  conversationId: string;
  content: string;
  timestamp: string;
  type: 'text' | 'image' | 'file' | 'system';
  isRead: boolean;
  isEdited?: boolean;
  replyTo?: string;
  attachments?: Attachment[];
}

export interface Attachment {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
}

export interface UserPresence {
  userId: string;
  isOnline: boolean;
  lastSeen?: string;
  status?: 'available' | 'busy' | 'away' | 'invisible';
}

export interface NotificationPayload {
  id: string;
  type: 'message' | 'emergency' | 'announcement' | 'voting' | 'maintenance';
  title: string;
  message: string;
  userId: string;
  timestamp: string;
  data?: Record<string, unknown>;
}

export interface WebSocketEvent {
  type: 'message' | 'presence' | 'notification' | 'typing' | 'message_read' | 'emergency_alert';
  data: Record<string, unknown>;
  timestamp: string;
}

class WebSocketService extends SimpleEventEmitter {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private currentUserId: string | null = null;
  private heartbeatInterval: ReturnType<typeof setTimeout> | null = null;
  private mockMode = true; // Set to false when real backend is available

  // Mock data for development
  private mockUsers = new Map<string, UserPresence>();
  private mockTypingUsers = new Set<string>();

  constructor() {
    super();
    this.setupMockUsers();
  }

  private setupMockUsers() {
    // Initialize mock users with online status
    const mockUserIds = ['1', '2', '3', '4', '5'];
    mockUserIds.forEach((userId) => {
      this.mockUsers.set(userId, {
        userId,
        isOnline: Math.random() > 0.3, // 70% chance of being online
        status: 'available',
        lastSeen: new Date().toISOString(),
      });
    });

    // Simulate users going online/offline
    setInterval(() => {
      if (this.mockMode) {
        this.simulatePresenceChanges();
      }
    }, 10000);
  }

  private simulatePresenceChanges() {
    this.mockUsers.forEach((user, userId) => {
      if (Math.random() > 0.9) {
        // 10% chance of status change
        const newStatus: UserPresence = {
          ...user,
          isOnline: !user.isOnline,
          lastSeen: new Date().toISOString(),
        };
        this.mockUsers.set(userId, newStatus);

        this.emit('presence', {
          type: 'presence',
          data: newStatus,
          timestamp: new Date().toISOString(),
        });
      }
    });
  }

  connect(userId: string, token?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnecting || this.isConnected()) {
        resolve();
        return;
      }

      this.currentUserId = userId;
      this.isConnecting = true;

      if (this.mockMode) {
        // Mock WebSocket connection
        setTimeout(() => {
          this.isConnecting = false;
          this.emit('connected');
          this.startHeartbeat();
          console.log(`[WebSocket] Mock connection established for user ${userId}`);
          resolve();
        }, 500);
        return;
      }

      try {
        const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8080';
        const url = `${wsUrl}/chat?userId=${userId}${token ? `&token=${token}` : ''}`;

        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          console.log('[WebSocket] Connected successfully');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.emit('connected');
          this.startHeartbeat();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data: WebSocketEvent = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('[WebSocket] Failed to parse message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('[WebSocket] Connection closed:', event.code, event.reason);
          this.isConnecting = false;
          this.stopHeartbeat();
          this.emit('disconnected');

          if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('[WebSocket] Connection error:', error);
          this.isConnecting = false;
          reject(error);
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private reconnect() {
    if (this.isConnecting || !this.currentUserId) return;

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(
      `[WebSocket] Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`,
    );

    setTimeout(() => {
      if (this.currentUserId) {
        this.connect(this.currentUserId);
      }
    }, delay);
  }

  private startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.mockMode) {
        // Mock heartbeat - just emit a heartbeat event
        this.emit('heartbeat');
      } else if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private handleMessage(event: WebSocketEvent) {
    switch (event.type) {
      case 'message':
        this.emit('message', event);
        break;
      case 'presence':
        this.emit('presence', event);
        break;
      case 'notification':
        this.emit('notification', event);
        break;
      case 'typing':
        this.emit('typing', event);
        break;
      case 'message_read':
        this.emit('messageRead', event);
        break;
      case 'emergency_alert':
        this.emit('emergencyAlert', event);
        break;
      default:
        console.warn('[WebSocket] Unknown message type:', event.type);
    }
  }

  sendMessage(message: Omit<ChatMessage, 'id' | 'timestamp'>): Promise<ChatMessage> {
    return new Promise((resolve, reject) => {
      const fullMessage: ChatMessage = {
        ...message,
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
      };

      if (this.mockMode) {
        // Mock sending message
        setTimeout(
          () => {
            this.emit('message', {
              type: 'message',
              data: fullMessage,
              timestamp: fullMessage.timestamp,
            });
            resolve(fullMessage);
          },
          100 + Math.random() * 200,
        );
        return;
      }

      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      try {
        this.ws.send(
          JSON.stringify({
            type: 'message',
            data: fullMessage,
          }),
        );
        resolve(fullMessage);
      } catch (error) {
        reject(error);
      }
    });
  }

  sendTyping(conversationId: string, isTyping: boolean) {
    const typingData = {
      conversationId,
      userId: this.currentUserId,
      isTyping,
      timestamp: new Date().toISOString(),
    };

    if (this.mockMode) {
      // Mock typing indicator
      if (isTyping) {
        this.mockTypingUsers.add(this.currentUserId || '');
      } else {
        this.mockTypingUsers.delete(this.currentUserId || '');
      }

      this.emit('typing', {
        type: 'typing',
        data: typingData,
        timestamp: typingData.timestamp,
      });
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'typing',
          data: typingData,
        }),
      );
    }
  }

  markMessageAsRead(messageId: string, conversationId: string) {
    const readData = {
      messageId,
      conversationId,
      userId: this.currentUserId,
      timestamp: new Date().toISOString(),
    };

    if (this.mockMode) {
      // Mock message read
      setTimeout(() => {
        this.emit('messageRead', {
          type: 'message_read',
          data: readData,
          timestamp: readData.timestamp,
        });
      }, 50);
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'message_read',
          data: readData,
        }),
      );
    }
  }

  updatePresence(status: UserPresence['status']) {
    if (!this.currentUserId) return;

    const presence: UserPresence = {
      userId: this.currentUserId,
      isOnline: true,
      status,
      lastSeen: new Date().toISOString(),
    };

    if (this.mockMode) {
      this.mockUsers.set(this.currentUserId, presence);
      this.emit('presence', {
        type: 'presence',
        data: presence,
        timestamp: presence.lastSeen,
      });
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'presence',
          data: presence,
        }),
      );
    }
  }

  getUserPresence(userId: string): UserPresence | null {
    if (this.mockMode) {
      return this.mockUsers.get(userId) || null;
    }

    // In real implementation, this would query the backend
    return null;
  }

  getAllUserPresences(): UserPresence[] {
    if (this.mockMode) {
      return Array.from(this.mockUsers.values());
    }

    return [];
  }

  isConnected(): boolean {
    if (this.mockMode) {
      return this.currentUserId !== null;
    }

    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  disconnect() {
    this.stopHeartbeat();

    if (this.mockMode) {
      this.currentUserId = null;
      this.emit('disconnected');
      console.log('[WebSocket] Mock connection closed');
      return;
    }

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.currentUserId = null;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  // Method to switch between mock and real mode
  setMockMode(enabled: boolean) {
    this.mockMode = enabled;
  }
}

// Singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
